let _ = t => t,
  _t;
import styled from 'academy-common-lib/utils/Styling';
import ACVideoPlayer from 'academy-common-lib/components/video/ACVideoPlayer';
import VideoProgressOverlay from './VideoProgressOverlay';
import { useCallback, useState, useEffect } from 'react';
import { usageTracker } from '../../../utils/usageTracker';
import { Metrics } from '../../../Metrics';
import { useLesson, useShorts } from '../../../hooks/contentHooks';
import ACThumbnailOverlay from 'academy-common-lib/components/card/ACThumbnailOverlay';
import { WatchOnHubSpotAcademyLink } from './WatchOnHubSpotAcademyLink';
import styledWithTheme from 'foundations-theming/utils';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const VideoWrapper = styledWithTheme.section(_t || (_t = _`
  position: relative;
`));
const PausedThumbnailOverlayHeaderContainer = styled.div.withConfig({
  displayName: "Video__PausedThumbnailOverlayHeaderContainer",
  componentId: "sc-1gfouul-0"
})(["border-bottom-right-radius:5px;border-top-right-radius:5px;background:rgba(0,0,0,0.5);padding:8px;margin-left:-12px;pointer-events:auto;"]);
const noop = () => {};
export const Video = ({
  lessonId,
  contentId,
  shortId,
  language,
  startTime = 0,
  onPlay = noop,
  onFinish = noop,
  onEngagement = noop,
  isPublic = false,
  renderContext,
  creatorHubId,
  enablePictureInPicture,
  hublet,
  link,
  videoType
}) => {
  var _lesson$content;
  const [showOverlayOnLoad, setShowOverlayOnLoad] = useState(true);
  const [paused, setPaused] = useState(false);
  const [hovered, setHovered] = useState(false);
  const {
    lesson
  } = useLesson(lessonId, language, isPublic);
  const {
    shortFormContent
  } = useShorts(shortId, isPublic);
  const content =
  // @ts-expect-error each content type has an id field
  (lesson === null || lesson === void 0 || (_lesson$content = lesson.content) === null || _lesson$content === void 0 ? void 0 : _lesson$content.find(c => c.id === contentId)) || shortFormContent;
  const playerType = 'HUBSPOT_VIDEO';
  const videoId = content === null || content === void 0 ? void 0 : content.reference;
  const handlePlay = useCallback(() => {
    usageTracker.track('interaction', {
      action: 'video-started',
      contentId,
      lessonId,
      shortId,
      isTimestamped: startTime > 0
    });
    Metrics.counter('video-started').increment();
    onPlay();
    setShowOverlayOnLoad(false);
    setPaused(false);
    usageTracker.track('videoUsage', {
      type: 'started',
      playerType,
      videoId,
      renderContext,
      creatorHubId,
      contentId,
      lessonId,
      shortId,
      sourceUrl: window.location !== window.parent.location ? document.referrer : document.location.href
    });
  }, [contentId, creatorHubId, setShowOverlayOnLoad, lessonId, onPlay, renderContext, shortId, startTime, videoId]);
  const handleLoaded = useCallback(() => {
    usageTracker.track('interaction', {
      action: 'video-loaded',
      contentId,
      lessonId,
      shortId,
      isTimestamped: startTime > 0
    });
    Metrics.counter('video-loaded').increment();
    usageTracker.track('videoUsage', {
      type: 'loaded',
      playerType,
      videoType,
      videoId,
      renderContext,
      creatorHubId,
      contentId,
      lessonId,
      shortId,
      sourceUrl: window.location !== window.parent.location ? document.referrer : document.location.href
    });
  }, [contentId, creatorHubId, lessonId, renderContext, shortId, startTime, videoId, videoType]);
  const handleActivation = useCallback(() => {
    onEngagement();
    usageTracker.track('interaction', {
      action: 'video-engagement',
      contentId,
      lessonId,
      isTimestamped: startTime > 0
    });
    Metrics.counter('video-activated').increment();
    usageTracker.track('videoUsage', {
      type: 'engaged',
      playerType,
      videoType,
      videoId,
      renderContext,
      creatorHubId,
      contentId,
      lessonId,
      shortId,
      sourceUrl: window.location !== window.parent.location ? document.referrer : document.location.href
    });
  }, [contentId, creatorHubId, lessonId, onEngagement, renderContext, startTime, videoId, videoType, shortId]);
  const handleFinish = useCallback(() => {
    usageTracker.track('interaction', {
      action: 'video-finished',
      contentId,
      lessonId,
      shortId,
      isTimestamped: startTime > 0
    });
    Metrics.counter('video-finished').increment();
    onFinish();
    usageTracker.track('videoUsage', {
      type: 'finished',
      playerType,
      videoType,
      videoId,
      renderContext,
      creatorHubId,
      contentId,
      lessonId,
      shortId,
      sourceUrl: window.location !== window.parent.location ? document.referrer : document.location.href
    });
  }, [contentId, creatorHubId, lessonId, onFinish, renderContext, shortId, startTime, videoId, videoType]);
  const handleMinutesWatched = useCallback(viewDuration => {
    usageTracker.track('videoUsage', {
      type: 'minutes-watched',
      playerType,
      videoId,
      viewDuration,
      renderContext,
      creatorHubId,
      contentId,
      videoType,
      lessonId,
      shortId,
      sourceUrl: window.location !== window.parent.location ? document.referrer : document.location.href
    });
  }, [contentId, creatorHubId, renderContext, videoId, videoType, lessonId, shortId]);
  useEffect(() => {
    if (content) {
      usageTracker.track('videoUsage', {
        type: 'rendered',
        playerType,
        videoId,
        renderContext,
        creatorHubId,
        contentId,
        videoType,
        lessonId,
        shortId,
        sourceUrl: window.location !== window.parent.location ? document.referrer : document.location.href
      });
    }
  }, [content, contentId, creatorHubId, playerType, renderContext, videoId, videoType, lessonId, shortId]);
  if (!content) return null;
  return /*#__PURE__*/_jsxs(VideoWrapper, {
    onMouseEnter: () => setHovered(true),
    onMouseLeave: () => setHovered(false),
    children: [/*#__PURE__*/_jsx(ACVideoPlayer, {
      creatorHubId: (content === null || content === void 0 ? void 0 : content.hubId) || undefined,
      creatorRegion: hublet,
      "data-test-id": "embedded-academy-video-player",
      enablePictureInPicture: enablePictureInPicture,
      onActivation: handleActivation,
      onFinish: handleFinish,
      onPause: () => setPaused(true),
      onPlay: handlePlay,
      onReady: handleLoaded,
      onTimeProgress: handleMinutesWatched,
      startTime: startTime,
      videoId: videoId
    }), !showOverlayOnLoad && (paused || hovered) && /*#__PURE__*/_jsx(ACThumbnailOverlay, {
      header: /*#__PURE__*/_jsx(PausedThumbnailOverlayHeaderContainer, {
        children: /*#__PURE__*/_jsx(WatchOnHubSpotAcademyLink, {
          href: link,
          onClick: () => {
            usageTracker.track('interaction', {
              action: 'watermark-link-to-academy-click',
              contentId,
              lessonId,
              shortId
            });
          },
          truncate: false
        })
      })
    }), showOverlayOnLoad && /*#__PURE__*/_jsx(VideoProgressOverlay, {
      contentId: contentId,
      isPublic: isPublic,
      language: language,
      lessonId: lessonId,
      link: link,
      shortId: shortId
    })]
  });
};